<template>
  <div data-testid="so-date-picker" v-bind="$attrs">
    <label>
      {{ label }}
      <v-date-picker
        v-if="type === 'timeslot'"
        v-model="internalValue"
        data-testid="so-date-picker-calendar"
        :allowed-dates="isDateAllowed"
        :min="minIso"
        :max="maxIso"
        :disabled="disabled"
        :class="{
          'so-date-picker-panel': true,
          'so-date-picker-panel--error': !!error,
        }"
        color="success"
        show-adjacent-months
      />

      <template v-else>
        <div class="d-flex align-center">
          <v-menu
            v-model="menuOpen"
            :close-on-content-click="false"
            :disabled="disabled"
            :min-width="0"
          >
            <template #activator="{ props: menuProps }">
              <v-text-field
                v-bind="menuProps"
                :model-value="displayValue"
                :placeholder="placeholder || defaultPlaceholder"
                :disabled="disabled"
                :error="!!error"
                :error-messages="error ? [error] : undefined"
                :rules="rules"
                validate-on="submit"
                readonly
                variant="solo"
                flat
                density="compact"
                color="success"
                hide-details="auto"
                data-testid="so-date-picker-input"
                append-inner-icon="$calendar"
                @keydown.enter="menuOpen = !menuOpen"
              />
            </template>

            <v-card data-testid="so-date-picker-calendar">
              <v-date-picker
                v-model="internalValue"
                :allowed-dates="isDateAllowed"
                :min="minIso"
                :max="maxIso"
                :disabled="disabled"
                :multiple="isRangeType ? 'range' : false"
                color="success"
                show-adjacent-months
                hide-header
                @update:model-value="onDatePickerUpdate"
              />

              <template
                v-if="type === 'datetimerange' && pendingRange.length >= 2"
              >
                <v-divider />
                <div class="d-flex pa-3 gap-2">
                  <v-text-field
                    v-model="pendingStartTime"
                    type="time"
                    label="Start time"
                    variant="outlined"
                    density="compact"
                    hide-details
                  />
                  <v-text-field
                    v-model="pendingEndTime"
                    type="time"
                    label="End time"
                    variant="outlined"
                    density="compact"
                    hide-details
                  />
                </div>
              </template>

              <template v-if="type === 'datetime'">
                <v-divider />
                <div class="pa-3">
                  <v-text-field
                    :model-value="pendingTime"
                    type="time"
                    label="Time"
                    variant="outlined"
                    density="compact"
                    hide-details
                    data-testid="so-date-picker-time"
                    @update:model-value="onTimeChange"
                  />
                </div>
              </template>

              <v-card-actions v-if="isRangeType">
                <v-spacer />
                <SoButton
                  title="Clear"
                  type="secondary"
                  data-testid="so-date-picker-clear"
                  @click="clearRange"
                />
                <SoButton
                  title="Confirm"
                  type="primary"
                  :disabled="!isRangeComplete"
                  data-testid="so-date-picker-confirm"
                  @click="confirmRange"
                />
              </v-card-actions>
            </v-card>
          </v-menu>
          <SoButton
            v-if="clearable && modelValue"
            type="icon"
            icon="close"
            class="flex-shrink-0"
            data-testid="so-date-picker-clear-button"
            @click="onClear"
          />
        </div>
      </template>
    </label>

    <div
      v-if="type === 'timeslot' && !!error"
      role="alert"
      aria-live="polite"
      class="text-error ml-4"
      style="min-height: 28px"
      data-testid="so-date-picker-error"
    >
      {{ error }}
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import dayjs from "dayjs"
  import { ValidationRule } from "@/types/ValidationRule"
  import SoButton from "@/components/formControls/SoButton.vue"

  type SoDatePickerType =
    "date" | "daterange" | "datetime" | "datetimerange" | "timeslot"

  export default defineComponent({
    components: { SoButton },
    inheritAttrs: false,
    props: {
      modelValue: {
        type: Object as PropType<Date | Date[] | null>,
        default: null,
      },
      type: {
        type: String as PropType<SoDatePickerType>,
        default: "date",
      },
      label: {
        type: String,
        default: null,
      },
      placeholder: {
        type: String,
        default: null,
      },
      error: {
        type: String,
        default: null,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      allowedDates: {
        type: Array as PropType<Date[]>,
        default: null,
      },
      minDate: {
        type: [Date, String] as PropType<Date | string>,
        default: null,
      },
      maxDate: {
        type: [Date, String] as PropType<Date | string>,
        default: null,
      },
      clearable: {
        type: Boolean,
        default: false,
      },
      isDateDisabled: {
        type: Function as PropType<(timestamp: number) => boolean>,
        default: null,
      },
      rules: {
        type: Array as PropType<ValidationRule[]>,
        default: () => [],
      },
      defaultTime: {
        type: String,
        default: "00:00",
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        menuOpen: false,
        pendingRange: [] as string[],
        pendingStartTime: "00:00",
        pendingEndTime: "23:59",
        pendingTime: this.defaultTime,
      }
    },
    computed: {
      defaultPlaceholder(): string {
        if (this.isRangeType) return "Select date range"
        if (this.type === "datetime") return "Select date and time"
        return "Select date"
      },
      isRangeType(): boolean {
        return this.type === "daterange" || this.type === "datetimerange"
      },
      isRangeComplete(): boolean {
        return this.pendingRange.length >= 2
      },
      internalValue: {
        get(): string | string[] | undefined {
          if (this.isRangeType) {
            return this.pendingRange
          }
          if (!this.modelValue) return undefined
          if (Array.isArray(this.modelValue)) {
            return this.modelValue.map((d) => this.dateToIso(d))
          }
          return this.dateToIso(this.modelValue)
        },
        set(newValue: string | string[] | null) {
          if (this.isRangeType) {
            this.pendingRange = Array.isArray(newValue) ? newValue : []
            return
          }

          if (!newValue) {
            this.$emit("update:modelValue", null)
            return
          }
          if (this.type === "datetime") {
            this.$emit(
              "update:modelValue",
              this.combineDateTime(newValue as string, this.pendingTime),
            )
            return
          }
          this.$emit("update:modelValue", this.isoToDate(newValue as string))
        },
      },
      displayValue(): string {
        if (
          this.menuOpen &&
          this.isRangeType &&
          this.pendingRange.length >= 2
        ) {
          const sorted = this.sortByDate(this.pendingRange)
          const start = dayjs(sorted[0]).format("DD MMM YYYY")
          const end = dayjs(sorted[sorted.length - 1]).format("DD MMM YYYY")
          return `${start} → ${end}`
        }

        if (!this.modelValue) return ""

        if (Array.isArray(this.modelValue)) {
          const sortedModel = this.sortByDate(this.modelValue)
          const format =
            this.type === "datetimerange" ? "DD MMM YYYY HH:mm" : "DD MMM YYYY"
          return sortedModel.map((d) => dayjs(d).format(format)).join(" → ")
        }
        if (this.type === "datetime") {
          return dayjs(this.modelValue).format("DD MMM YYYY HH:mm")
        }
        return dayjs(this.modelValue).format("DD MMM YYYY")
      },
      minIso(): string | undefined {
        return this.minDate
          ? dayjs(this.minDate).format("YYYY-MM-DD")
          : undefined
      },
      maxIso(): string | undefined {
        return this.maxDate
          ? dayjs(this.maxDate).format("YYYY-MM-DD")
          : undefined
      },
      isValid(): boolean {
        if (!this.modelValue || !this.allowedDates) return true
        const dates = Array.isArray(this.modelValue)
          ? this.modelValue.map((d) => d.toDateString())
          : [this.modelValue.toDateString()]
        return dates.every((d) =>
          this.allowedDates.some((a) => a.toDateString() === d),
        )
      },
    },
    watch: {
      isValid(value: boolean) {
        if (!value) {
          this.$emit("update:modelValue", null)
        }
      },
      menuOpen(open: boolean) {
        if (this.type === "datetime") {
          if (open && this.modelValue instanceof Date) {
            this.pendingTime = dayjs(this.modelValue).format("HH:mm")
          }
          return
        }
        if (open && this.isRangeType && Array.isArray(this.modelValue)) {
          this.pendingRange = this.expandDateRange(
            this.dateToIso(this.modelValue[0]),
            this.dateToIso(this.modelValue[1]),
          )
          if (this.type === "datetimerange") {
            this.pendingStartTime = dayjs(this.modelValue[0]).format("HH:mm")
            this.pendingEndTime = dayjs(this.modelValue[1]).format("HH:mm")
          }
        } else if (open && this.isRangeType) {
          this.pendingRange = []
          this.pendingStartTime = "00:00"
          this.pendingEndTime = "23:59"
        } else if (!open && this.isRangeType && this.isRangeComplete) {
          this.confirmRange()
        }
      },
    },
    methods: {
      expandDateRange(start: string, end: string): string[] {
        const dates: string[] = []
        const sorted = [start, end].sort()
        let current = dayjs(sorted[0])
        const endDate = dayjs(sorted[1])
        const MAX_DAYS = 366
        while (
          (current.isBefore(endDate, "day") ||
            current.isSame(endDate, "day")) &&
          dates.length < MAX_DAYS
        ) {
          dates.push(current.format("YYYY-MM-DD"))
          current = current.add(1, "day")
        }
        return dates
      },
      dateToIso(date: Date): string {
        return dayjs(date).format("YYYY-MM-DD")
      },
      isoToDate(iso: string): Date {
        return dayjs(iso).toDate()
      },
      isDateAllowed(val: unknown): boolean {
        const date = dayjs(val as string).startOf("day")

        if (this.minDate) {
          const min = dayjs(this.minDate).startOf("day")
          if (date.isBefore(min)) return false
        }
        if (this.maxDate) {
          const max = dayjs(this.maxDate).startOf("day")
          if (date.isAfter(max)) return false
        }

        if (typeof this.isDateDisabled === "function") {
          // isDateDisabled returns true when disabled; isDateAllowed returns true when allowed — invert
          return !this.isDateDisabled(date.valueOf())
        }

        if (this.allowedDates) {
          return this.allowedDates.some(
            (allowed) =>
              allowed.toDateString() === date.toDate().toDateString(),
          )
        }
        return true
      },
      onDatePickerUpdate() {
        if (this.type === "date") {
          this.menuOpen = false
        }
      },
      combineDateTime(value: string | Date, time: string): Date {
        const [hours, minutes] = time.split(":").map((v) => Number(v) || 0)
        return dayjs(value).hour(hours).minute(minutes).second(0).toDate()
      },
      onTimeChange(time: string) {
        this.pendingTime = time
        if (this.modelValue instanceof Date) {
          this.$emit(
            "update:modelValue",
            this.combineDateTime(this.dateToIso(this.modelValue), time),
          )
        }
      },
      sortByDate(dates: (string | Date)[]): (string | Date)[] {
        return [...dates].sort(
          (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf(),
        )
      },
      confirmRange() {
        if (this.pendingRange.length < 2) return

        const sorted = this.sortByDate(this.pendingRange)
        const startDate = dayjs(sorted[0])
        const endDate = dayjs(sorted[sorted.length - 1])

        if (this.type === "datetimerange") {
          this.$emit("update:modelValue", [
            this.combineDateTime(sorted[0], this.pendingStartTime),
            this.combineDateTime(
              sorted[sorted.length - 1],
              this.pendingEndTime,
            ),
          ])
        } else {
          this.$emit("update:modelValue", [
            startDate.toDate(),
            endDate.toDate(),
          ])
        }
        this.menuOpen = false
      },
      clearRange() {
        this.pendingRange = []
        this.$emit("update:modelValue", null)
        this.menuOpen = false
      },
      onClear() {
        this.$emit("update:modelValue", null)
        this.menuOpen = false
      },
    },
  })
</script>

<style scoped>
  .so-date-picker-panel {
    border: 1px solid rgba(var(--v-theme-on-surface), var(--v-disabled-opacity));
    transition: border 250ms;
  }
  .so-date-picker-panel:hover {
    border: 1px solid rgb(var(--v-theme-on-surface));
  }
  .so-date-picker-panel:focus {
    outline: var(--theme-outline);
  }
  .so-date-picker-panel--error {
    border: 1px solid rgb(var(--v-theme-error));
  }
  .so-date-picker-panel--error:hover {
    border: 1px solid rgb(var(--v-theme-error));
  }
  :deep(.v-field--variant-solo) {
    height: 48px;
    min-width: 200px;
    border-radius: var(--theme-radius);
    transition: border 250ms;
    border: 1px solid rgb(var(--v-theme-border));
  }
  :deep(.v-field--variant-solo .v-field__input) {
    top: 2px;
  }
  :deep(.v-field--variant-solo:hover) {
    border: 1px solid rgb(var(--v-theme-on-surface));
  }
  :deep(.v-field--variant-solo.v-field--focused) {
    border-color: transparent;
  }

  :deep(.v-field--focused) {
    outline: var(--theme-outline);
  }
  :deep(.v-field--error) {
    border: 1px solid rgb(var(--v-theme-error)) !important;
  }

  :deep(.v-field--variant-outlined) {
    border-radius: var(--theme-radius);
  }
  :deep(.v-messages__message) {
    line-height: normal;
  }
</style>
